import { FC, memo, useCallback, useEffect, useMemo, useRef } from "react";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import { PRODUCT_SLUG } from "@types";
import CourseCardItem from "./CourseCardItem";
import { ICardTab } from "types/card-tab";
import { CourseCardSkeleton } from "@features/home/components/Skeleton/CourseCardSkeleton";

interface PropTypes {
  slug: PRODUCT_SLUG;
  activeTab: ICardTab | null;
  onReachEnd?: () => void;
  isLoading?: boolean;
  loadingMore?: boolean;
  firstLoad?: boolean;
  loadedProduct?: boolean;
}

const SKELETON_ITEMS = Array.from({ length: 6 });

const MobileCourseSwiper: FC<PropTypes> = ({
  slug,
  activeTab,
  onReachEnd,
  isLoading = false,
  loadingMore = false,
  firstLoad = false,
  loadedProduct = false,
}) => {
  const swiperRef = useRef<SwiperCore | null>(null);

  const products = activeTab?.products ?? [];
  const activeTabId = activeTab?.id;

  const showCourse = products.length > 0 && loadedProduct;

  const updateSwiper = useCallback(
    (callback?: (swiper: SwiperCore) => void) => {
      requestAnimationFrame(() => {
        const swiper = swiperRef.current;

        if (!swiper || swiper.destroyed) return;

        swiper.update();
        callback?.(swiper);
      });
    },
    []
  );

  useEffect(() => {
    updateSwiper((swiper) => {
      swiper.slideTo(0, 0);
    });
  }, [activeTabId, updateSwiper]);

  useEffect(() => {
    if (!isLoading && products.length && !firstLoad) {
      updateSwiper();
    }
  }, [isLoading, products.length, firstLoad, updateSwiper]);

  const handleReachEnd = useCallback(() => {
    if (isLoading || loadingMore) return;

    onReachEnd?.();
  }, [isLoading, loadingMore, onReachEnd]);

  const skeletonSlides = useMemo(
    () =>
      SKELETON_ITEMS.map((_, index) => (
        <SwiperSlide key={`skeleton-${index}`} className="!w-auto pb-1">
          <CourseCardSkeleton />
        </SwiperSlide>
      )),
    []
  );

  return (
    <div className="min-w-0 w-full overflow-hidden break-words bg-white courseCard relative">
      <Swiper
        key={`${activeTabId ?? "empty"}-${showCourse ? products.length : "loading"}`}
        id={`${slug}-mobile`}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        className={`swiper-${slug}-mobile mb-2`}
        modules={[FreeMode]}
        slidesPerView="auto"
        spaceBetween={16}
        slidesPerGroup={1}
        watchSlidesProgress={false}
        passiveListeners
        freeMode={{
          enabled: true,
          momentum: true,
          sticky: false,
        }}
        resistance={false}
        followFinger
        threshold={2}
        touchRatio={1}
        onReachEnd={handleReachEnd}
      >
        {!showCourse
          ? skeletonSlides
          : products.map((product) => (
              <SwiperSlide key={product.id} className="!w-auto">
                <CourseCardItem
                  product={product}
                  slug={slug}
                  imageWidth={160}
                />
              </SwiperSlide>
            ))}

        {isLoading && showCourse && (
          <SwiperSlide className="flex items-center justify-center !w-auto">
            {/* Loading spinner */}
          </SwiperSlide>
        )}
      </Swiper>
    </div>
  );
};

export default memo(MobileCourseSwiper);
